import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button, ConfirmModal, Input, Select, Card, CardContent } from "@/components/ui";
import {
  DataTable,
  DataTablePrimaryText,
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";
import {
  readerPresetKeys,
  readerPresetService,
} from "@/services/readerPresetService";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import type { ReaderPreset, ReaderPresetCreate } from "@/types";
import { Permission } from "@/types";
import { useAuthStore } from "@/store/authStore";

const EMPTY_FORM: ReaderPresetCreate = {
  brand: "",
  model: "",
  provider: "SUMUP",
  flow: "CLOUD_POS",
};

type SortColumn = "brand" | "model" | "provider" | "flow";

export default function ReaderPresetsPage() {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const queryClient = useQueryClient();
  const canHardDelete = useAuthStore(
    (s) =>
      s.hasPermission(Permission.PLATFORM_HARD_DELETE) &&
      s.hasPermission(Permission.READERS_PRESETS_UPDATE),
  );
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReaderPreset | null>(null);
  const [form, setForm] = useState<ReaderPresetCreate>(EMPTY_FORM);

  const {
    data: presets = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: readerPresetKeys.lists(),
    queryFn: () => readerPresetService.getAll(),
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: ReaderPresetCreate) => {
      if (editingPresetId) {
        return readerPresetService.update(editingPresetId, payload);
      }
      return readerPresetService.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: readerPresetKeys.lists() });
      toast.success(
        editingPresetId ? t("presets.updated") : t("presets.created"),
      );
      setEditingPresetId(null);
      setForm(EMPTY_FORM);
    },
    onError: (err) => {
      toastApiError(err, "saveReaderPreset");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (presetId: string) => readerPresetService.delete(presetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: readerPresetKeys.lists() });
      setPendingDelete(null);
      toast.success(t("presets.deleted"));
    },
    onError: (err) => {
      toastApiError(err, "deleteReaderPreset");
    },
  });

  const resetForm = () => {
    setEditingPresetId(null);
    setForm(EMPTY_FORM);
  };

  const validateForm = () => {
    if (!form.brand.trim() || !form.model.trim() || !form.provider || !form.flow) {
      toast.error(t("presets.validationRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveMutation.mutate({
      brand: form.brand.trim(),
      model: form.model.trim(),
      provider: form.provider.trim().toUpperCase(),
      flow: form.flow.trim().toUpperCase(),
    });
  };

  const handleEdit = (preset: ReaderPreset) => {
    setEditingPresetId(preset.id);
    setForm({
      brand: preset.brand,
      model: preset.model,
      provider: preset.provider,
      flow: preset.flow,
    });
  };

  const loadError =
    error != null ? getApiErrorMessage(error, "loadReaderPresets") : null;

  const columns = useMemo<DataTableColumn<ReaderPreset, SortColumn>[]>(
    () => [
      {
        id: "brand",
        header: t("presets.colBrand"),
        sortKey: "brand",
        sortKind: "text",
        getSortValue: (row) => row.brand,
        defaultWidth: 160,
        cell: (preset) => (
          <DataTablePrimaryText>{preset.brand}</DataTablePrimaryText>
        ),
      },
      {
        id: "model",
        header: t("presets.colModel"),
        sortKey: "model",
        sortKind: "text",
        getSortValue: (row) => row.model,
        defaultWidth: 160,
        cell: (preset) => preset.model,
      },
      {
        id: "provider",
        header: t("presets.colProvider"),
        sortKey: "provider",
        sortKind: "text",
        getSortValue: (row) => row.provider,
        defaultWidth: 120,
        cell: (preset) => (
          <span className="text-[13px] text-surface-500">{preset.provider}</span>
        ),
      },
      {
        id: "flow",
        header: t("presets.colFlow"),
        sortKey: "flow",
        sortKind: "text",
        getSortValue: (row) => row.flow,
        defaultWidth: 120,
        cell: (preset) => (
          <span className="font-mono text-xs text-surface-500">{preset.flow}</span>
        ),
      },
      createActionsColumn(t("presets.colActions"), {
        cell: (preset) => (
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => handleEdit(preset)}
              aria-label={t("presets.editTitle")}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {canHardDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setPendingDelete(preset)}
                className="text-error-700 hover:bg-error-50 hover:text-error-800"
                aria-label={t("presets.deleteTitle")}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        ),
      }),
    ],
    [canHardDelete, t],
  );

  return (
    <div className="space-y-6">
      <Card className="animate-slide-up">
        <CardContent className="px-5 py-4">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 xl:flex-row xl:items-center"
      >
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            type="text"
            value={form.brand}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, brand: e.target.value }))
            }
            placeholder={t("presets.brandPlaceholder")}
            aria-label={t("presets.brandPlaceholder")}
          />
          <Input
            type="text"
            value={form.model}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, model: e.target.value }))
            }
            placeholder={t("presets.modelPlaceholder")}
            aria-label={t("presets.modelPlaceholder")}
          />
          <Select
            value={form.provider}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, provider: e.target.value }))
            }
            placeholder={t("presets.providerPlaceholder")}
            aria-label={t("presets.providerPlaceholder")}
            options={[
              { value: "SUMUP", label: "SUMUP" },
              { value: "MOCK", label: "MOCK" },
            ]}
          />
          <Select
            value={form.flow}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, flow: e.target.value }))
            }
            placeholder={t("presets.flowPlaceholder")}
            aria-label={t("presets.flowPlaceholder")}
            options={[{ value: "CLOUD_POS", label: "CLOUD_POS" }]}
          />
        </div>
        <div className="flex shrink-0 justify-end gap-2">
          {editingPresetId && (
            <Button
              type="button"
              variant="outline"
              onClick={resetForm}
            >
              {tCommon("cancel")}
            </Button>
          )}
          <Button
            type="submit"
            isLoading={saveMutation.isPending}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            {editingPresetId ? t("presets.save") : t("presets.add")}
          </Button>
        </div>
      </form>
        </CardContent>
      </Card>

      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={presets}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="brand"
        preferencesKey="reader-presets"
        loadState={
          isLoading
            ? { status: "loading" }
            : loadError != null
              ? {
                  status: "error",
                  message: loadError,
                  retryLabel: tCommon("tryAgain"),
                  onRetry: () => void refetch(),
                }
              : undefined
        }
        emptyMessage={t("presets.empty")}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
       
        isOpen={pendingDelete != null}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        title={t("presets.deleteTitle")}
        message={t("presets.deleteConfirm", {
          name: pendingDelete
            ? `${pendingDelete.brand} ${pendingDelete.model}`
            : "",
        })}
        confirmText={tCommon("delete")}
        isDestructive
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
